import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaShieldAlt, FaHandshake, FaGlobeAmericas, FaTruck, FaArrowRight } from 'react-icons/fa';
import B2BEntryModal from '../../Components/buyer/B2BEntryModal';
import ProductCard from '../../Components/buyer/ProductCard';
import Loader from '../../Components/utils/Loader';
import SEO from '../../Components/utils/SEO';

// Max categories to show on B2B Home (2 rows × 5 cols = 10)
const MAX_HOME_CATS = 10;

const API = process.env.REACT_APP_Backend_Host_Name;

const B2BHome = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [featuredListings, setFeaturedListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchCategory, setSearchCategory] = useState('');

    useEffect(() => {
        if (!localStorage.getItem('b2b_acknowledged')) {
            setShowModal(true);
        }
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [catRes, listRes] = await Promise.all([
                axios.get(`${API}/api/v1/categories?b2b=true`),
                axios.get(`${API}/api/v1/b2b/listings?limit=8&sort=best_selling`)
            ]);
            setCategories(catRes.data.categories || []);
            setFeaturedListings(listRes.data.listings || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = () => {
        localStorage.setItem('b2b_acknowledged', 'true');
        setShowModal(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (searchQuery) params.set('query', searchQuery);
        if (searchCategory) params.set('category', searchCategory);
        navigate(`/b2b/products-services${params.toString() ? `?${params.toString()}` : ''}`);
    };

    if (loading) return <Loader type="simpleMax" />;

    return (
        <div className="b2bPage b2bHomePage">
            <SEO
                title="B2B wholesale marketplace"
                description="Source wholesale products and services from verified suppliers. Compare listings, MOQ, and shipping options on Chocowi B2B."
                canonicalPath="/b2b"
            />
            {showModal && <B2BEntryModal onContinue={handleContinue} />}

            <div className="b2bHero">
                <div className="heroContent">
                    <h1>B2B Wholesale Marketplace</h1>
                    <p>Source products in bulk from verified manufacturers, exporters, and wholesalers at competitive wholesale prices.</p>
                    <form className="b2bSearchBar" onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder="Search wholesale products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {/* <select value={searchCategory} onChange={(e) => setSearchCategory(e.target.value)}>
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat._id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select> */}
                        <button type="submit"><FaSearch /></button>
                    </form>
                    <div className="b2bHeroStats">
                        <div className="heroStat">
                            <strong>{categories.length}+</strong>
                            <span>B2B Categories</span>
                        </div>
                        <div className="heroStat">
                            <strong>{featuredListings.length > 0 ? `${featuredListings.length}+` : '0'}</strong>
                            <span>Active Listings</span>
                        </div>
                        <div className="heroStat">
                            <strong>Verified</strong>
                            <span>Suppliers Only</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="b2bTrustBar">
                <div className="trustItem">
                    <div className="trustIcon"><FaShieldAlt /></div>
                    Verified Suppliers
                </div>
                <div className="trustItem">
                    <div className="trustIcon"><FaHandshake /></div>
                    Secure Transactions
                </div>
                <div className="trustItem">
                    <div className="trustIcon"><FaGlobeAmericas /></div>
                    Global Sourcing
                </div>
                <div className="trustItem">
                    <div className="trustIcon"><FaTruck /></div>
                    Bulk Shipping
                </div>
            </div>

            <div className="mainSection">
                {categories.length > 0 && (
                    <div className="b2bCategorySection">
                        <div className="b2bSectionTitleRow">
                            <h2 className="b2bSectionTitle">Browse B2B Categories</h2>
                        </div>
                        <div className="b2bCategoryGrid">
                            {categories.slice(0, MAX_HOME_CATS).map(cat => (
                                <div
                                    key={cat._id}
                                    className="b2bCategoryCard"
                                    onClick={() => navigate(`/b2b/categories`)}
                                >
                                    <span className="catIcon">{cat.icon || '📦'}</span>
                                    <div className="catName">{cat.name}</div>
                                    <div className="catCount">{cat.subcategories?.length || 0} subcategories</div>
                                </div>
                            ))}
                        </div>
                        <div className="b2bSeeAllWrapper">
                            <Link to="/b2b/categories" className="seeAllBtn">
                                See All Categories <FaArrowRight />
                            </Link>
                        </div>
                    </div>
                )}

                {featuredListings.length > 0 && (
                    <div className="b2bCategorySection">
                        <h2 className="b2bSectionTitle">Featured Products</h2>
                        <div className="b2bListingGrid">
                            {featuredListings.map(listing => (
                                <ProductCard key={listing._id} b2bListing={listing} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default B2BHome;
